import { type ComponentProps, useMemo, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import Table, { useTableQuery } from "@/v2-backoffice-client/components/table"
import {
  UPTIME_COLUMN_SIZING,
  UPTIME_DEFAULT_VISIBILITY,
  UPTIME_TABLE_COLUMNS,
} from "@/v2-backoffice-client/uptime/uptime.columns"
import {
  type UptimeBucket,
  type UptimeGroupStats,
} from "@/v2-backoffice-client/uptime/uptime.buckets.utils"
import UptimeBulkMuteAction from "@/v2-backoffice-client/uptime/mute/UptimeBulkMuteAction"
import UptimeStatusFilter from "@/v2-backoffice-client/uptime/UptimeStatusFilter"
import { categoryAnchorId } from "@/v2-backoffice-client/uptime/UptimeCategoryNav"
import {
  formatLatency,
  getCategoryDescription,
  getCategoryLabel,
  getPlatformLabel,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import { useUptimeTargetsListQuery } from "@/v2-backoffice-client/uptime/uptime.list.query"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import { useSearchParams } from "@/v2-backoffice-client/utils"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"
import type { UptimePlatform } from "@/v2-backoffice-client/uptime/uptime.constants"
import {
  EMPTY_UPTIME_BUCKET_COUNTS,
  PAGE_SIZE,
  URL_PARAM_CATEGORY,
  readBucketFromUrl,
} from "@/v2-backoffice-client/uptime/uptime.list.utils"

export type UptimeCategoryTableProps = ComponentProps<"section"> & {
  // Compound (platform, source) key the sidebar selected. Drives
  // the localStorage tableId + DOM anchor so v1 and v2 splits of
  // the same source persist their layout independently.
  groupKey: string
  source: string
  // Platform this group belongs to. Used for the section heading
  // ("Placepoint v1 / Production Apps") so an operator scoping into
  // the legacy stack always sees that context, even when the source
  // alone (`app.release`) is ambiguous.
  platform: UptimePlatform
  // Pre-computed group summary from the board (uptime % / median
  // latency / counts). The body of the table is server-paginated;
  // the section header keeps using the board's single-pass summary
  // so the "5/12 up · 3 down · median 240ms" strip never flickers
  // mid-fetch.
  summary: UptimeGroupStats
}

// Initial sort: same severity desc as the incident view so down rows
// always lead the page even after the operator drills in. They can
// re-sort by any column from there; the table persists per-tableId.
const INITIAL_SORTING = [{ id: "Status", desc: true }]

function UptimeCategoryTable(props: UptimeCategoryTableProps) {
  const { groupKey, source, platform, summary, className, ...sectionProps } =
    props

  const opener = useAppViewSheetOpener()

  // Initial pill state is read directly from the URL so the row
  // filter, the pill UI and the URL all agree on first paint.
  // Default is "all" -- per-category pages are explorers, not
  // triage queues, so the operator probably wants to see the full
  // category. URL persistence (via `urlParam`) makes deep-linking
  // to "all the down basemap layers" still trivial.
  const [searchParams] = useSearchParams()
  const [bucket, setBucket] = useState<UptimeBucket>(() =>
    readBucketFromUrl(searchParams, URL_PARAM_CATEGORY),
  )

  // Server-side pagination scoped to the (platform, source) the
  // sidebar selected, plus the active pill bucket. The board's
  // full-fleet fetch already has the per-source slice cached, but
  // routing the table through the paginated endpoint keeps the
  // table's pagination/sort/filter behaviour identical to every
  // other backoffice list view.
  const variables = useMemo(
    () => ({ scope: { platform, source, bucket } }),
    [platform, source, bucket],
  )
  const {
    items,
    pageInfo,
    loading,
    error,
    data,
    refetch,
    onFetchMore,
    onQueryChange,
  } = useTableQuery({
    pageSize: PAGE_SIZE,
    useQuery: useUptimeTargetsListQuery,
    select: (d) => d.getUptimeTargetsList,
    variables,
  })

  const counts =
    data?.getUptimeTargetsList?.bucketCounts ?? EMPTY_UPTIME_BUCKET_COUNTS

  const label = getCategoryLabel(source)
  const description = getCategoryDescription(source)
  const platformLabel = getPlatformLabel(platform)

  const healthy = summary.down === 0 && summary.slow === 0
  let headerVariant: "success-1" | "destructive" | "warning" = "success-1"
  if (!healthy) {
    headerVariant = summary.down > 0 ? "destructive" : "warning"
  }

  // Stable tableId per (platform, source) -- "uptime-category-..."
  // ASCII-only Source / Platform values guarantee a localStorage-
  // safe key without escaping. Persisted layout is per-category so
  // an operator who customises the maplayer view doesn't
  // accidentally hide the "Last error" column they need on
  // basemaps; including `platform` keeps v1 / v2 splits of the same
  // source on independent persistence keys.
  const tableId = `uptime-category-${groupKey.replace(/[^a-zA-Z0-9]+/g, "-")}`

  return (
    <section
      id={categoryAnchorId(groupKey)}
      className={cn("uptime-category-table-wrap", className)}
      aria-label={`${platformLabel} · ${label}`}
      {...sectionProps}
    >
      <header className="uptime-category-table-header">
        <div className="uptime-category-table-title">
          <div className="uptime-category-table-title-platform">
            {platformLabel}
          </div>
          <div className="uptime-category-table-title-name">{label}</div>
          {description && (
            <div className="uptime-category-table-title-desc">
              {description}
            </div>
          )}
        </div>
        <div className="uptime-category-table-summary">
          <Dot variant={headerVariant} />
          <span>
            <span className="font-medium text-foreground tabular-nums">
              {summary.up}
            </span>
            /{summary.total} up
            {summary.down > 0 && (
              <span className="text-destructive ml-2">
                · {summary.down} down
              </span>
            )}
            {summary.slow > 0 && (
              <span className="text-amber-500 ml-2">· {summary.slow} slow</span>
            )}
            {summary.skipped > 0 && (
              <span className="ml-2">· {summary.skipped} skipped</span>
            )}
            {summary.muted > 0 && (
              <span className="text-muted-foreground/80 ml-2">
                · +{summary.muted} muted
              </span>
            )}
          </span>
          <span className="ml-2 text-muted-foreground/80">
            {summary.aggregateUptimePercent == null
              ? ""
              : `${summary.aggregateUptimePercent.toFixed(2)}% · `}
            {summary.medianLatencyMs == null
              ? "no latency data"
              : `median ${formatLatency(summary.medianLatencyMs)}`}
          </span>
        </div>
      </header>

      <Table<UptimeTargetFragment>
        className="uptime-category-table"
        tableId={tableId}
        data={items}
        columns={UPTIME_TABLE_COLUMNS}
        initialColumnSizing={UPTIME_COLUMN_SIZING}
        initialSorting={INITIAL_SORTING}
        defaultColumnVisibility={UPTIME_DEFAULT_VISIBILITY}
        enableGrouping
        enableRowSelection
        manualSorting
        manualFiltering
        onQueryChange={onQueryChange}
        getRowId={(t) => `${t.Source}|${t.Identifier}`}
        getHighlightedRowId={(row) =>
          opener.getIsActive(
            "uptime",
            `${row.original.Source}|${row.original.Identifier}`,
          )
        }
      >
        <Table.Toolbar>
          <UptimeStatusFilter
            counts={counts}
            urlParam={URL_PARAM_CATEGORY}
            onChange={setBucket}
          />
          <div className="flex-1" />
          <Table.Search column="Name" placeholder="Search by name…" />
          <Table.GroupSelector />
          <Table.ColumnButton />
          <Separator orientation="vertical" />
          <Table.ToolbarMenu
            onRefresh={refetch}
            exportFileName={`uptime-${groupKey}`}
          />
        </Table.Toolbar>

        <Table.FilterList />
        <Table.GroupList />

        <Table.Content
          isLoading={loading}
          error={error}
          pageInfo={pageInfo}
          onFetchMore={onFetchMore}
          renderEmpty={
            <div className="text-sm text-muted-foreground text-center py-10">
              {bucket === "all" && "No targets in this category yet."}
              {bucket === "down" && "No targets are currently down."}
              {bucket === "slow" && "No targets are slower than usual."}
              {bucket === "drift" && "No layer configuration drifts detected."}
              {bucket === "muted" && "No targets are currently muted."}
            </div>
          }
        />

        <Table.SelectionToolbar<UptimeTargetFragment>>
          {(selected, reset) => (
            <>
              <Table.ExportSelectionButton
                fileName={`uptime-${groupKey}`}
                selectionCount={selected.length}
              />
              <UptimeBulkMuteAction
                selectedRows={selected}
                onComplete={reset}
              />
            </>
          )}
        </Table.SelectionToolbar>
      </Table>
    </section>
  )
}

export default UptimeCategoryTable
